import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SocialLinks from '../components/SocialLinks';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-page">
      <header className="glass-header px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-text-secondary hover:text-primary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <img src={LOGO_URL} alt="My Direct Debits" className="h-8 w-8" />
          <span className="font-heading font-semibold text-dark">Privacy Policy</span>
        </div>
      </header>

      <main className="px-4 py-8">
        <div className="max-w-3xl mx-auto card-default p-8">
          <h1 className="font-heading font-bold text-dark text-3xl mb-2">Privacy Policy</h1>
          <p className="text-text-light text-sm mb-8">Last updated: January 2025</p>

          <div className="prose prose-green max-w-none space-y-6 text-text-secondary">
            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">1. Introduction</h2>
              <p>
                Voliciti Group Ventures Ltd ("we", "us", or "our") operates the My Direct Debits application 
                (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard 
                your information when you use our Service.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">2. Information We Collect</h2>
              <p>We collect information you provide directly to us, including:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Name and email address when you create an account</li>
                <li>Direct debit information you enter (company names, amounts, payment dates)</li>
                <li>Payment information when you upgrade to Premium (processed securely by Stripe)</li>
                <li>Feedback and communications you send to us</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide, maintain, and improve our Service</li>
                <li>Send you reminders about upcoming direct debits</li>
                <li>Process your transactions and manage your subscription</li>
                <li>Respond to your comments, questions, and provide customer service</li>
                <li>Send you technical notices and security alerts</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">4. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. Your 
                data is encrypted in transit using TLS and at rest using industry-standard encryption.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">5. Data Retention</h2>
              <p>
                We retain your personal information for as long as your account is active or as needed 
                to provide you services. You can delete your account at any time from within the app, 
                which will permanently remove all your data from our systems.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">6. Third-Party Services</h2>
              <p>
                We use third-party services that may collect information about you:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Stripe</strong> - for payment processing</li>
                <li><strong>Brevo (Sendinblue)</strong> - for email communications</li>
              </ul>
              <p className="mt-2">
                These services have their own privacy policies governing the use of your information.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">7. Cookies and Tracking Technologies</h2>
              <p>
                We gather personal data through your engagement with us and our website, utilising cookies 
                and similar technologies. Cookies are small text files placed on your device that help us 
                provide and improve our service.
              </p>
              <p className="mt-2">We use the following types of cookies:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Essential cookies</strong> — required for the platform to function (e.g. authentication tokens)</li>
                <li><strong>Analytics cookies</strong> — help us understand how users interact with the platform to improve the experience</li>
                <li><strong>Third-party cookies</strong> — set by our service providers (Stripe, Brevo) for payment processing and email delivery</li>
              </ul>
              <p className="mt-2">
                You can control cookies through your browser settings. Disabling essential cookies may affect 
                the functionality of the service.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">8. Do Not Sell or Share My Personal Information</h2>
              <p>
                Certain privacy regulations (including the California Consumer Privacy Act (CCPA), UK GDPR, 
                and other applicable data protection laws) may classify the distribution of personal data for 
                targeted advertising, based on your activities across various websites, as "sales," "sharing," 
                or "targeted advertising."
              </p>
              <p className="mt-2">
                <strong>We do not sell your personal information.</strong> We do not share your personal data 
                with third-party advertisers for targeted advertising purposes. Your information is only shared 
                with service providers (Stripe for payments, Brevo for transactional emails) strictly for the 
                purpose of delivering our service to you.
              </p>
              <p className="mt-2">
                Depending on your location, you may have the right to opt out of the sale or sharing of your 
                personal data. As we do not engage in such practices, no opt-out action is required. However, 
                if you have any concerns or wish to exercise any rights under applicable privacy regulations, 
                you may contact us at <strong>info@mddreminder.uk</strong>.
              </p>
              <p className="mt-2">
                If any changes to our practices occur in the future, we will update this policy and provide 
                clear opt-out mechanisms in accordance with applicable laws.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">9. Your Rights</h2>
              <p>Under applicable data protection laws (including UK GDPR, EU GDPR, and CCPA), you have the right to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data (you can also delete your account directly within the app)</li>
                <li>Object to processing of your data</li>
                <li>Request data portability</li>
                <li>Opt out of the sale or sharing of personal information (where applicable)</li>
                <li>Not be discriminated against for exercising your privacy rights</li>
              </ul>
              <p className="mt-2">
                To exercise any of these rights, please contact us at <strong>info@mddreminder.uk</strong>. 
                We will respond to your request within 30 days.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">10. Children's Privacy</h2>
              <p>
                Our Service is not intended for use by children under the age of 16. We do not knowingly 
                collect personal information from children under 16.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-dark text-xl mb-3">12. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Voliciti Group Ventures Ltd</strong><br />
                Email: info@mddreminder.uk
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="px-4 py-6 text-center">
        <div className="mb-3">
          <SocialLinks />
        </div>
        <p className="text-text-light text-sm">
          © 2026 Voliciti Group Ventures Ltd. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
